import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/pages/admin/components/AdminLayout';
import { supabase } from '@/lib/supabase';
import OrderDetailModal from './OrderDetailModal';

interface OrderRow {
  id: string;
  order_no: string;
  user_id: string;
  items: Array<{ name: string; quantity: number; points: number; spec?: string; image?: string }>;
  total_amount: number;
  address: { name?: string; phone?: string; detail?: string } | null;
  status: string;
  created_at: string;
  logistics_company?: string;
  tracking_no?: string;
  shipped_at?: string;
  custom_users?: { nickname: string | null; phone: string | null } | null;
}

const statusStyle: Record<string, string> = {
  '待付款': 'bg-gray-100 text-gray-500',
  '待发货': 'bg-amber-50 text-amber-600',
  '待收货': 'bg-sky-50 text-sky-600',
  '已完成': 'bg-emerald-50 text-emerald-600',
  '已取消': 'bg-gray-100 text-gray-400',
};

const tabs = ['全部', '待付款', '待发货', '待收货', '已完成'];

const LOGISTICS_COMPANIES = [
  '顺丰速运', '中通快递', '圆通速递', '申通快递',
  '韵达快递', '京东物流', '邮政EMS', '德邦物流', '其他',
];

const AdminOrdersPage = () => {
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('全部');
  const [search, setSearch] = useState('');
  const [detailOrder, setDetailOrder] = useState<OrderRow | null>(null);
  const [shipModalOrder, setShipModalOrder] = useState<OrderRow | null>(null);
  const [shipForm, setShipForm] = useState({ company: '', tracking_no: '' });
  const [shipSubmitting, setShipSubmitting] = useState(false);
  const [shipSuccess, setShipSuccess] = useState(false);
  const [shipError, setShipError] = useState('');

  // 删除相关状态
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<OrderRow | null>(null); // 单条删除
  const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('orders')
      .select('*, custom_users(nickname, phone)')
      .order('created_at', { ascending: false });
    if (!error && data) setOrders(data as OrderRow[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const openShipModal = (order: OrderRow) => {
    setShipModalOrder(order);
    setShipForm({ company: '顺丰速运', tracking_no: '' });
    setShipError('');
    setShipSuccess(false);
  };

  const handleShipSubmit = async () => {
    if (!shipModalOrder) return;
    if (!shipForm.company.trim()) { setShipError('请选择物流公司'); return; }
    if (!shipForm.tracking_no.trim()) { setShipError('请输入快递单号'); return; }
    setShipSubmitting(true);
    setShipError('');
    const { error } = await supabase
      .from('orders')
      .update({
        status: '待收货',
        logistics_company: shipForm.company.trim(),
        tracking_no: shipForm.tracking_no.trim(),
        shipped_at: new Date().toISOString(),
      })
      .eq('id', shipModalOrder.id);
    setShipSubmitting(false);
    if (error) { setShipError('发货失败，请重试'); return; }
    await supabase.from('admin_logs').insert({
      action: '发货',
      target_type: '订单',
      target_id: shipModalOrder.id,
      detail: { orderNo: shipModalOrder.order_no, trackingNo: shipForm.tracking_no.trim(), company: shipForm.company.trim() },
    });
    await fetchOrders();
    setShipSuccess(true);
  };

  // 单条删除
  const handleDeleteOne = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await supabase.from('orders').delete().eq('id', deleteTarget.id);
    await supabase.from('admin_logs').insert({
      action: '删除订单',
      target_type: '订单',
      target_id: deleteTarget.id,
      detail: { orderNo: deleteTarget.order_no },
    });
    setDeleting(false);
    setDeleteTarget(null);
    setSelectedIds((prev) => { const s = new Set(prev); s.delete(deleteTarget.id); return s; });
    await fetchOrders();
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    const ids = Array.from(selectedIds);
    await supabase.from('orders').delete().in('id', ids);
    await supabase.from('admin_logs').insert({
      action: '批量删除订单',
      target_type: '订单',
      target_id: ids[0],
      detail: { count: ids.length, ids },
    });
    setDeleting(false);
    setShowBatchDeleteConfirm(false);
    setSelectedIds(new Set());
    await fetchOrders();
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const filtered = orders.filter((o) => {
    const matchTab = activeTab === '全部' || o.status === activeTab;
    const q = search.trim().toLowerCase();
    const matchSearch = !q || o.order_no.toLowerCase().includes(q) ||
      (o.custom_users?.nickname ?? '').toLowerCase().includes(q) ||
      (o.address?.name ?? '').toLowerCase().includes(q);
    return matchTab && matchSearch;
  });

  const allSelected = filtered.length > 0 && filtered.every((o) => selectedIds.has(o.id));
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => {
        const s = new Set(prev);
        filtered.forEach((o) => s.delete(o.id));
        return s;
      });
    } else {
      setSelectedIds((prev) => {
        const s = new Set(prev);
        filtered.forEach((o) => s.add(o.id));
        return s;
      });
    }
  };

  const formatItems = (items: OrderRow['items']) => {
    if (!Array.isArray(items) || items.length === 0) return '-';
    const first = items[0];
    const rest = items.length > 1 ? ` 等${items.length}件` : '';
    return `${first.name ?? '商品'} x${first.quantity ?? 1}${rest}`;
  };

  const formatTime = (ts: string) =>
    new Date(ts).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });

  const counts = tabs.slice(1).reduce((acc, t) => {
    acc[t] = orders.filter((o) => o.status === t).length;
    return acc;
  }, {} as Record<string, number>);

  const exportOrdersCSV = () => {
    const headers = ['订单号', '会员昵称', '手机号', '商品', '积分', '收货人', '收货电话', '地址', '状态', '物流公司', '快递单号', '下单时间'];
    const rows = filtered.map(o => [
      o.order_no,
      o.custom_users?.nickname ?? '',
      o.custom_users?.phone ?? '',
      Array.isArray(o.items) ? o.items.map(i => `${i.name}x${i.quantity}`).join('+') : '',
      Number(o.total_amount),
      o.address?.name ?? '',
      o.address?.phone ?? '',
      o.address?.detail ?? '',
      o.status,
      o.logistics_company ?? '',
      o.tracking_no ?? '',
      new Date(o.created_at).toLocaleString('zh-CN'),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `订单数据_${new Date().toLocaleDateString('zh-CN')}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout title="订单管理">
      {/* 统计卡片 */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {[
          { label: '待发货', count: counts['待发货'] ?? 0, color: 'text-amber-500', bg: 'bg-amber-50', icon: 'ri-time-line' },
          { label: '待收货', count: counts['待收货'] ?? 0, color: 'text-sky-500', bg: 'bg-sky-50', icon: 'ri-truck-line' },
          { label: '已完成', count: counts['已完成'] ?? 0, color: 'text-emerald-500', bg: 'bg-emerald-50', icon: 'ri-checkbox-circle-line' },
          { label: '全部订单', count: orders.length, color: 'text-gray-600', bg: 'bg-gray-50', icon: 'ri-file-list-3-line' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:shadow-sm transition-shadow"
            onClick={() => setActiveTab(s.label === '全部订单' ? '全部' : s.label)}>
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${s.bg} flex-shrink-0`}>
              <i className={`${s.icon} ${s.color} text-lg`} />
            </div>
            <div>
              <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
              <p className="text-xs text-gray-400">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs + Search + 操作栏 */}
      <div className="flex items-center justify-between mb-4 gap-4">
        <div className="flex items-center gap-1 bg-white rounded-xl p-1.5 flex-wrap">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap transition-all ${
                activeTab === t ? 'bg-rose-500 text-white font-medium' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t}
              {t !== '全部' && counts[t] > 0 && (
                <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${activeTab === t ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                  {counts[t]}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 w-56">
            <i className="ri-search-line text-gray-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索订单号/会员/收货人"
              className="text-sm outline-none flex-1 placeholder-gray-400"
            />
            {search && (
              <button onClick={() => setSearch('')} className="text-gray-300 hover:text-gray-500 cursor-pointer">
                <i className="ri-close-line text-sm" />
              </button>
            )}
          </div>
          {selectedIds.size > 0 && (
            <button
              onClick={() => setShowBatchDeleteConfirm(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap bg-rose-500 text-white hover:bg-rose-600 transition-all"
            >
              <i className="ri-delete-bin-line text-sm" />删除所选({selectedIds.size})
            </button>
          )}
          <button
            onClick={exportOrdersCSV}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap bg-white border border-gray-200 text-gray-600 hover:border-rose-300 transition-all"
          >
            <i className="ri-download-line text-sm" />导出Excel
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="px-4 py-3.5 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded accent-rose-500 cursor-pointer"
                  />
                </th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3.5">订单号</th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3.5">会员</th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3.5">商品</th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3.5">积分</th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3.5">收货地址</th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3.5">状态</th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3.5">下单时间</th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3.5">操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o, i) => (
                <tr
                  key={o.id}
                  className={`${i < filtered.length - 1 ? 'border-b border-gray-50' : ''} transition-colors ${selectedIds.has(o.id) ? 'bg-rose-50/40' : 'hover:bg-gray-50/50'}`}
                >
                  <td className="px-4 py-4 w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(o.id)}
                      onChange={() => toggleSelect(o.id)}
                      className="w-4 h-4 rounded accent-rose-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600 font-mono whitespace-nowrap">{o.order_no}</td>
                  <td className="px-4 py-4">
                    <p className="text-sm text-gray-800 font-medium whitespace-nowrap">{o.custom_users?.nickname ?? '未知用户'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{o.custom_users?.phone ?? '-'}</p>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600 max-w-[140px]">
                    <span className="line-clamp-2">{formatItems(o.items)}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm font-bold text-emerald-600">{Number(o.total_amount).toLocaleString()}</span>
                    <span className="text-xs text-emerald-500 ml-0.5">积分</span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-500 max-w-[140px]">
                    <p className="line-clamp-1">{o.address?.name ?? '-'} {o.address?.phone ?? ''}</p>
                    <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{o.address?.detail ?? '-'}</p>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${statusStyle[o.status] ?? 'bg-gray-100 text-gray-500'}`}>
                      {o.status}
                    </span>
                    {o.tracking_no && (
                      <p className="text-xs text-gray-400 mt-1 font-mono">{o.logistics_company} {o.tracking_no}</p>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-400 whitespace-nowrap">{formatTime(o.created_at)}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setDetailOrder(o)}
                        className="text-xs text-rose-500 cursor-pointer hover:underline whitespace-nowrap"
                      >
                        详情
                      </button>
                      {o.status === '待发货' && (
                        <button
                          onClick={() => openShipModal(o)}
                          className="text-xs bg-emerald-500 text-white px-2.5 py-1 rounded-full cursor-pointer hover:bg-emerald-600 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-truck-line mr-1" />发货
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteTarget(o)}
                        className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-300 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer"
                        title="删除订单"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && filtered.length === 0 && (
          <div className="py-16 text-center">
            <i className="ri-file-list-3-line text-4xl text-gray-200 block mb-3" />
            <p className="text-gray-400 text-sm">暂无订单数据</p>
          </div>
        )}
      </div>

      {!loading && filtered.length > 0 && (
        <div className="mt-4 text-xs text-gray-400 text-right">
          共 {filtered.length} 条订单
          {selectedIds.size > 0 && <span className="ml-2 text-rose-400">已选 {selectedIds.size} 条</span>}
        </div>
      )}

      {/* 订单详情弹窗 */}
      {detailOrder && (
        <OrderDetailModal
          order={detailOrder}
          onClose={() => setDetailOrder(null)}
          onShip={(order) => { setDetailOrder(null); openShipModal(order); }}
        />
      )}

      {/* 发货弹窗 */}
      {shipModalOrder && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !shipSubmitting && !shipSuccess && setShipModalOrder(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              {shipSuccess ? (
                <div className="flex flex-col items-center py-4">
                  <div className="w-20 h-20 flex items-center justify-center bg-emerald-50 rounded-full mb-4"
                    style={{ animation: 'popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
                    <i className="ri-check-line text-emerald-500 text-4xl" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">发货成功！</h3>
                  <p className="text-sm text-gray-500 text-center mb-1">订单状态已更新为「待收货」</p>
                  <div className="bg-gray-50 rounded-xl px-4 py-3 w-full mt-3 mb-5 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">物流公司</span>
                      <span className="text-gray-700 font-medium">{shipForm.company}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">快递单号</span>
                      <span className="text-gray-700 font-mono font-medium">{shipForm.tracking_no}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">订单号</span>
                      <span className="text-gray-500 font-mono text-xs">{shipModalOrder.order_no}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setShipModalOrder(null)}
                    className="w-full bg-emerald-500 text-white py-3 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap"
                  >
                    完成
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-truck-line text-emerald-500 text-xl" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900">订单发货</h3>
                      <p className="text-xs text-gray-400 font-mono">{shipModalOrder.order_no}</p>
                    </div>
                  </div>
                  {shipModalOrder.address && (
                    <div className="bg-gray-50 rounded-xl px-4 py-3 mb-4">
                      <div className="flex items-start gap-2">
                        <i className="ri-map-pin-line text-gray-400 text-sm mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            {shipModalOrder.address.name}
                            <span className="text-gray-400 font-normal ml-2">{shipModalOrder.address.phone}</span>
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{shipModalOrder.address.detail}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        物流公司 <span className="text-rose-500">*</span>
                      </label>
                      <select
                        value={shipForm.company}
                        onChange={(e) => { setShipForm((p) => ({ ...p, company: e.target.value })); setShipError(''); }}
                        className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-emerald-400 bg-white cursor-pointer"
                      >
                        <option value="">请选择物流公司</option>
                        {LOGISTICS_COMPANIES.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        快递单号 <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={shipForm.tracking_no}
                        onChange={(e) => { setShipForm((p) => ({ ...p, tracking_no: e.target.value })); setShipError(''); }}
                        placeholder="请输入快递单号"
                        className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-emerald-400 font-mono"
                      />
                    </div>
                  </div>
                  {shipError && (
                    <p className="text-xs text-rose-500 flex items-center gap-1 mt-3">
                      <i className="ri-error-warning-line" />{shipError}
                    </p>
                  )}
                  <div className="flex gap-3 mt-5">
                    <button
                      onClick={() => setShipModalOrder(null)}
                      disabled={shipSubmitting}
                      className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleShipSubmit}
                      disabled={shipSubmitting || !shipForm.company || !shipForm.tracking_no.trim()}
                      className="flex-1 bg-emerald-500 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-emerald-600 transition-colors disabled:opacity-50"
                    >
                      {shipSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          发货中...
                        </span>
                      ) : (
                        <span><i className="ri-truck-line mr-1" />确认发货</span>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* 单条删除确认弹窗 */}
      {deleteTarget && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !deleting && setDeleteTarget(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}
              style={{ animation: 'popIn 0.2s ease' }}>
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-rose-50 rounded-full mb-4">
                  <i className="ri-delete-bin-line text-rose-500 text-2xl" />
                </div>
                <h3 className="font-bold text-gray-900 text-base mb-1">确认删除订单？</h3>
                <p className="text-sm text-gray-500 mb-1">此操作不可恢复</p>
                <div className="bg-gray-50 rounded-xl px-4 py-2.5 w-full mt-2 mb-5">
                  <p className="text-xs text-gray-400 font-mono">{deleteTarget.order_no}</p>
                  <p className="text-sm text-gray-700 font-medium mt-0.5">{deleteTarget.custom_users?.nickname ?? '未知用户'}</p>
                </div>
                <div className="flex gap-3 w-full">
                  <button
                    onClick={() => setDeleteTarget(null)}
                    disabled={deleting}
                    className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleDeleteOne}
                    disabled={deleting}
                    className="flex-1 bg-rose-500 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-rose-600 transition-colors disabled:opacity-50"
                  >
                    {deleting ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        删除中...
                      </span>
                    ) : '确认删除'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 批量删除确认弹窗 */}
      {showBatchDeleteConfirm && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !deleting && setShowBatchDeleteConfirm(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}
              style={{ animation: 'popIn 0.2s ease' }}>
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-rose-50 rounded-full mb-4">
                  <i className="ri-delete-bin-line text-rose-500 text-2xl" />
                </div>
                <h3 className="font-bold text-gray-900 text-base mb-1">批量删除订单</h3>
                <p className="text-sm text-gray-500 mb-4">
                  即将删除 <span className="text-rose-500 font-bold">{selectedIds.size}</span> 条订单，此操作不可恢复
                </p>
                <div className="flex gap-3 w-full">
                  <button
                    onClick={() => setShowBatchDeleteConfirm(false)}
                    disabled={deleting}
                    className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleBatchDelete}
                    disabled={deleting}
                    className="flex-1 bg-rose-500 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-rose-600 transition-colors disabled:opacity-50"
                  >
                    {deleting ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        删除中...
                      </span>
                    ) : `确认删除 ${selectedIds.size} 条`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes popIn { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>
    </AdminLayout>
  );
};

export default AdminOrdersPage;
